import type { SemanticType } from '../../../semanticTypes'
import type { ConditionalFunctionString } from '../../conditions.types'
import { phasesMatchingDuration, phasesMatchingName } from '../runtimeHelpers'
import { semanticTypeRuntimePath } from './utils'

export const PHASE_NAME_OPERATORS = [
  'IS_EQUAL',
  'IS_NOT_EQUAL',
  'INCLUDES',
  'EXCLUDES',
  'NOT_SET'
] as const

export type PhaseNameOperator = (typeof PHASE_NAME_OPERATORS)[number]

export const PHASE_DURATION_OPERATORS = [
  'SHORTER_THAN',
  'LONGER_THAN',
  'EQUALS',
  'DOES_NOT_EQUAL',
  'IS_OPEN_ENDED'
] as const

export type PhaseDurationOperator = (typeof PHASE_DURATION_OPERATORS)[number]

export type PhaseDurationValue = {
  years: number
  months: number
  days: number
}

export type PhaseNameConditionValue = {
  operator?: PhaseNameOperator
  name?: string
}

export type PhaseDurationConditionValue = {
  operator?: PhaseDurationOperator
  duration?: PhaseDurationValue
}

export type PhaseConditionValue =
  | PhaseNameConditionValue
  | PhaseDurationConditionValue

export const isPhaseNameOperator = (
  operator: unknown
): operator is PhaseNameOperator =>
  PHASE_NAME_OPERATORS.includes(operator as PhaseNameOperator)

export const isPhaseDurationOperator = (
  operator: unknown
): operator is PhaseDurationOperator =>
  PHASE_DURATION_OPERATORS.includes(operator as PhaseDurationOperator)

export const readPhaseNameValue = (value: unknown): PhaseNameConditionValue => {
  if (typeof value !== 'object' || value === null) return {}
  const candidate = value as PhaseNameConditionValue
  return {
    operator: isPhaseNameOperator(candidate.operator)
      ? candidate.operator
      : undefined,
    name: typeof candidate.name === 'string' ? candidate.name : undefined
  }
}

export const readPhaseDurationValue = (
  value: unknown
): PhaseDurationConditionValue => {
  if (typeof value !== 'object' || value === null) return {}
  const candidate = value as PhaseDurationConditionValue
  return {
    operator: isPhaseDurationOperator(candidate.operator)
      ? candidate.operator
      : undefined,
    duration: readDuration(candidate.duration)
  }
}

export const phaseNameFilterExpression = ({
  property,
  value
}: {
  property: string
  value: unknown
}): string => {
  const { operator, name } = readPhaseNameValue(value)
  if (!operator) return '[]'
  if (operator !== 'NOT_SET' && !name?.trim()) return '[]'
  return phasesMatchingName(property, JSON.stringify({ operator, name }))
}

export const phaseName: ConditionalFunctionString = params =>
  `${phaseNameFilterExpression(params)}.length > 0`

export type PhaseDurationFunctionStringConfig = {
  startDateSemanticType: SemanticType
}

export const phaseDurationFilterExpression = (
  config: PhaseDurationFunctionStringConfig,
  { property, value }: { property: string; value: unknown }
): string => {
  const { operator, duration } = readPhaseDurationValue(value)
  if (!operator) return '[]'
  if (operator !== 'IS_OPEN_ENDED' && !hasPhaseDuration(duration)) return '[]'
  return phasesMatchingDuration(
    property,
    JSON.stringify({ operator, duration }),
    semanticTypeRuntimePath(config.startDateSemanticType)
  )
}

export const phaseDuration =
  (config: PhaseDurationFunctionStringConfig): ConditionalFunctionString =>
  params =>
    `${phaseDurationFilterExpression(config, params)}.length > 0`

const readDuration = (value: unknown): PhaseDurationValue | undefined => {
  if (typeof value !== 'object' || value === null) return undefined
  const candidate = value as Partial<PhaseDurationValue>
  return {
    years: Number(candidate.years ?? 0),
    months: Number(candidate.months ?? 0),
    days: Number(candidate.days ?? 0)
  }
}

export const hasPhaseDuration = (
  duration: PhaseDurationValue | undefined
): boolean =>
  Boolean(
    duration &&
      (duration.years > 0 || duration.months > 0 || duration.days >= 0)
  )
